
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export const useCancelSubscription = (refreshData: () => void) => {
  const { toast } = useToast();

  // Cancel subscription function with refund option
  return useCallback(async (
    userId: string, 
    stripeSubscriptionId: string | null, 
    shouldRefund: boolean = false
  ) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Authentication required');
      }
      
      console.log('Calling cancel-subscription with params:', { 
        userId, 
        stripeSubscriptionId, 
        shouldRefund 
      });
      
      // Call the Supabase Edge Function to cancel the subscription
      const { data, error: functionError } = await supabase.functions.invoke('cancel-subscription', {
        body: { 
          userId,
          stripeSubscriptionId,
          shouldRefund
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
      
      console.log('Response from cancel-subscription function:', data);
      
      if (functionError) {
        console.error('Error calling cancel-subscription function:', functionError);
        throw new Error('Failed to cancel subscription: ' + functionError.message);
      }
      
      if (data?.error) {
        console.error('Error from function:', data.error);
        throw new Error(data.error);
      }
      
      toast({
        title: 'Subscription canceled',
        description: data?.refunded 
          ? "The customer's subscription has been canceled with a refund"
          : "The customer's subscription has been canceled successfully"
      });
      
      // Refresh data with a longer delay to ensure database updates are complete
      setTimeout(() => {
        refreshData();
        // Add another refresh after a longer delay to ensure data is completely updated
        setTimeout(refreshData, 1500);
      }, 750); 
      
      return true;
    } catch (err: any) {
      console.error('Error canceling subscription:', err);
      toast({
        title: 'Cancellation failed',
        description: err.message || 'An error occurred while canceling the subscription',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast, refreshData]);
};
